import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { pinLogin } from '../../lib/api';
import { ShoppingCart, Loader2, Delete, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function PosLogin() {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // company_id scopes this POS terminal to one tenant.
  // Set via URL: /pos/login?cid=comp-abc, or persisted in localStorage after first visit.
  const [companyId, setCompanyId] = useState(() => {
    const fromUrl = searchParams.get('cid');
    if (fromUrl) {
      localStorage.setItem('pos_company_id', fromUrl);
      return fromUrl;
    }
    return localStorage.getItem('pos_company_id') || null;
  });

  // Step 1: enter company code. Step 2: enter PIN.
  const [companyInput, setCompanyInput] = useState('');
  const [step, setStep] = useState(companyId ? 'pin' : 'company');

  useEffect(() => {
    document.documentElement.classList.add('pos-login-shell');
    document.body.classList.add('pos-login-shell');
    document.getElementById('root')?.classList.add('pos-login-shell');

    return () => {
      document.documentElement.classList.remove('pos-login-shell');
      document.body.classList.remove('pos-login-shell');
      document.getElementById('root')?.classList.remove('pos-login-shell');
    };
  }, []);

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    const code = companyInput.trim();
    if (!code) return;
    localStorage.setItem('pos_company_id', code);
    setCompanyId(code);
    setStep('pin');
  };

  const handleResetCompany = () => {
    localStorage.removeItem('pos_company_id');
    setCompanyId(null);
    setCompanyInput('');
    setPin('');
    setStep('company');
  };

  const handlePinInput = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      
      // Vibrate on touch (mobile)
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
      
      // Auto-submit when PIN is 4+ digits
      if (newPin.length >= 4) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  const handleClear = () => {
    setPin('');
  };

  const handleSubmit = async (pinToSubmit) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await pinLogin(pinToSubmit || pin, companyId);
      
      if (response.success) {
        login(response.user, response.token);
        if (navigator.vibrate) {
          navigator.vibrate([50, 50, 50]);
        }
        toast.success(`Willkommen, ${response.user.name}!`);
        navigate('/pos');
      } else {
        toast.error('Ungültiger PIN');
        setPin('');
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Anmeldung fehlgeschlagen');
      setPin('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] overflow-hidden bg-[#1a2f38] text-white">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-sm flex-col px-4 pb-safe pt-3 sm:px-6 sm:pt-5">
        <div className="flex items-center justify-center py-3 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-[1.6rem] bg-white/10 backdrop-blur sm:h-16 sm:w-16">
              <ShoppingCart className="h-7 w-7 text-green-400 sm:h-8 sm:w-8" />
            </div>
            <div>
              <h1 className="text-[2rem] font-bold leading-none sm:text-[2.2rem]">FiscalPOS</h1>
              <p className="mt-1 text-base text-white/60 sm:text-lg">Kellner Login</p>
            </div>
          </div>
        </div>

        {step === 'company' ? (
          <div className="flex flex-1 flex-col justify-between pb-4">
            <form onSubmit={handleCompanySubmit} className="flex flex-1 flex-col justify-center gap-5">
              <div className="space-y-2 text-center">
                <p className="text-xl font-semibold">Unternehmens-Code</p>
                <p className="text-sm text-white/55">Geben Sie den Code Ihres Betriebs ein</p>
              </div>
              <input
                type="text"
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                placeholder="z.B. comp-deinbetrieb"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-gramm="false"
                data-gramm_editor="false"
                autoFocus
                className="w-full rounded-[1.75rem] border border-white/15 bg-white/10 px-4 py-4 text-center
                         font-mono text-lg text-white placeholder-white/30 backdrop-blur
                         focus:outline-none focus:border-green-400"
              />
              <button
                type="submit"
                disabled={!companyInput.trim()}
                className="w-full rounded-[1.75rem] bg-green-500 py-4 text-lg font-bold text-white transition-all
                         hover:bg-green-400 disabled:opacity-40 touch-manipulation"
              >
                Weiter
              </button>
            </form>

            <div className="pt-4 text-center">
              <a
                href="/admin/login"
                className="inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/75"
              >
                <ArrowLeft className="h-4 w-4" />
                Manager Portal
              </a>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col pb-3">
            <div className="space-y-4 pt-1 text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="max-w-[65vw] truncate rounded-full bg-white/10 px-4 py-2 text-sm font-mono text-white/75">
                  {companyId}
                </span>
                <button
                  type="button"
                  onClick={handleResetCompany}
                  className="text-sm font-medium text-white/45 underline-offset-4 transition-colors hover:text-white/65 hover:underline"
                >
                  Ändern
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex justify-center gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-4 w-4 rounded-full border-2 transition-all duration-200 ${
                        pin.length > i
                          ? 'scale-110 border-green-400 bg-green-400'
                          : 'border-white/40'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-white/55">
                  {pin.length === 0 ? 'PIN eingeben' : `${pin.length} von 4 Ziffern`}
                </p>
              </div>
            </div>

            <div className="mt-auto space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handlePinInput(String(digit))}
                    disabled={isLoading}
                    className="aspect-[1/0.88] rounded-[1.75rem] bg-white/10 text-[2rem] font-bold backdrop-blur
                             transition-all duration-100 active:scale-95 active:bg-white/20
                             hover:bg-white/15 disabled:opacity-50 touch-manipulation select-none"
                    data-testid={`pin-${digit}`}
                  >
                    {digit}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={isLoading || pin.length === 0}
                  className="aspect-[1/0.88] rounded-[1.75rem] bg-red-500/16 text-2xl font-bold text-red-300
                           transition-all duration-100 active:scale-95 active:bg-red-500/25
                           hover:bg-red-500/20 disabled:opacity-30 touch-manipulation select-none"
                >
                  C
                </button>
                <button
                  type="button"
                  onClick={() => handlePinInput('0')}
                  disabled={isLoading}
                  className="aspect-[1/0.88] rounded-[1.75rem] bg-white/10 text-[2rem] font-bold backdrop-blur
                           transition-all duration-100 active:scale-95 active:bg-white/20
                           hover:bg-white/15 disabled:opacity-50 touch-manipulation select-none"
                  data-testid="pin-0"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={handleBackspace}
                  disabled={isLoading || pin.length === 0}
                  className="flex aspect-[1/0.88] items-center justify-center rounded-[1.75rem] bg-white/10 text-white
                           backdrop-blur transition-all duration-100 active:scale-95 active:bg-white/20
                           hover:bg-white/15 disabled:opacity-30 touch-manipulation select-none"
                >
                  <Delete className="h-7 w-7" />
                </button>
              </div>

              <div className="flex min-h-[2rem] items-center justify-center">
                {isLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin text-green-400" />
                ) : (
                  <a
                    href="/admin/login"
                    className="inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/75"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Manager Portal
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
